import { useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { BoardColumn } from '@rundown/types';
import { reorderByRank, useOptimisticMutation } from '../../lib/optimistic';
import { boardKeys, moveCard, type BoardCard, type BoardList } from './api';

export interface BoardMoveVariables {
  readonly movedId: string;
  readonly column: BoardColumn;
  readonly beforeId?: string;
  readonly afterId?: string;
}

/**
 * Drag-to-move for the Board, wired through the shared optimistic mutation
 * helper (apps/web/src/lib/optimistic.ts) — same lifecycle BacklogPage's
 * `useBacklogRank` uses, applied to a list that is grouped by column instead
 * of flat.
 *
 * `reorderByRank` operates on one ordered array; the cache here holds every
 * card on the Board in every column. `applyOptimistically` below therefore
 * gives the moved card its new `boardColumn` directly (reorderByRank does not
 * need to know about columns at all — it only ever reads/writes rank) and
 * lets `reorderByRank` place it among its new column's cards by rank. Every
 * OTHER card's position is untouched, so a card dragged from Triage to Ready
 * does not perturb the order of the four columns it never touched.
 */
export function useBoardMove(workspaceId: string): UseMutationResult<BoardCard, Error, BoardMoveVariables> {
  const queryClient = useQueryClient();
  const listKey = boardKeys.list(workspaceId);

  return useOptimisticMutation<BoardMoveVariables, BoardCard>({
    queryClient,
    mutationFn: ({ movedId, column, beforeId, afterId }) =>
      moveCard(workspaceId, movedId, { column, beforeId, afterId }),
    affectedQueryKeys: [listKey],
    applyOptimistically: (qc, variables) => {
      const previous = qc.getQueryData<BoardList>(listKey);
      if (previous === undefined) return;

      // reorderByRank expects `items` sorted highest-rank-first — true of the
      // whole cache today because board.service.ts's `list()` returns it that
      // way already (ordered by column, then rank descending within it), and
      // reorderByRank only ever compares the moved card's neighbours by id, so
      // the presence of other columns' cards in the same array does not
      // confuse it.
      const withNewColumn = previous.cards.map((card) =>
        card.id === variables.movedId ? { ...card, boardColumn: variables.column } : card,
      );

      const reordered = reorderByRank(
        withNewColumn,
        variables.movedId,
        variables,
        (card) => card.id,
        (card) => card.boardRank,
        (card, boardRank) => ({ ...card, boardRank }),
      );

      // `sprint` and `outsideThePlan` come along unchanged: a drag moves a card
      // between columns, which is neither a change of week nor work escaping
      // the plan.
      qc.setQueryData(listKey, { ...previous, cards: reordered });
    },
    reconcile: (qc, result) => {
      const current = qc.getQueryData<BoardList>(listKey);
      if (current === undefined) return;
      // Merge the server's row (its authoritative boardColumn/boardRank
      // included) into the list already showing it in the right place — not
      // an invalidate. See optimistic.ts's file header for why.
      qc.setQueryData(listKey, {
        ...current,
        cards: current.cards.map((card) => (card.id === result.id ? { ...card, ...result } : card)),
      });
    },
  });
}
